import { FC, useState } from 'react';
import {
  Appbar,
  Button,
  Dialog,
  IconText,
  AlertDialog,
  Spacing,
  Typography,
  Divider,
  StyledDiv,
  ItemInfoProps,
  ItemInfoList,
  Menu,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import {
  analyticsService,
  EventNames,
  syncStateManager,
  userCache,
} from '@taleemabad/db';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { ProfileAction } from '@taleemabad/shared';

interface UserProfileProps {
  userInformation: ItemInfoProps[];
  schoolInformation: ItemInfoProps[];
  appVersion?: string;
  downloadStatus?: string;
  onLogoutClick: () => void;
  onResetAppClick: () => void;
  onDeactivateAccountClick: () => void;
}

const menuData = [
  { id: 1, name: 'Logout', value: 'logout' },
  { id: 2, name: 'Delete Account', value: 'delete' },
];

enum DownloadStatus {
  Downloaded = 'Downloaded',
  Downloading = 'Downloading',
}

export const UserProfile: FC<UserProfileProps> = ({
  userInformation,
  schoolInformation,
  appVersion,
  downloadStatus,
  onLogoutClick,
  onResetAppClick,
  onDeactivateAccountClick,
}) => {
  const [showLogoutPopup, setShowLogoutPopup] = useState<boolean>(false);
  const [showResetPopup, setShowResetPopup] = useState<boolean>(false);
  const [showNetworkError, setShowNetworkError] = useState<boolean>(false);
  const [showDeactivatePopup, setShowDeactivatePopup] =
    useState<boolean>(false);

  const [openMenu, setOpenMenu] = useState(false);

  const handleResetButtonClicked = () => {
    if (syncStateManager.isConnected) {
      setShowResetPopup(true);
    } else {
      setShowNetworkError(true);
      analyticsService.trackEvent(EventNames.noInternetNoInternetPopup, {});
    }
  };

  const handleDeactivate = () => {
    if (syncStateManager.isConnected) {
      onDeactivateAccountClick?.();
      setShowDeactivatePopup(false);
    } else {
      setShowDeactivatePopup(false);
      analyticsService.trackEvent(EventNames.noInternetNoInternetPopup, {});
    }
  };

  const selectedRegion = userCache.getRegionFromCache();
  const handleMenuClick = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setOpenMenu((prev) => !prev);
  };
  return (
    <>
      <AppBarContainer>
        <Appbar
          title="Profile Settings"
          leadingIcon="person"
          actions={[
            {
              icon: 'more_vert',
              onClick: (event) => handleMenuClick(event),
            },
          ]}
        />
        <Menu
          open={openMenu}
          menuCorner="end-end"
          anchorCorner="end-end"
          menuItems={menuData}
          positioning="absolute"
          yOffset={-80}
          onSelectedItem={(data) => {
            if (!syncStateManager.isConnected) {
              setShowNetworkError(true);
              return;
            }
            if (data.value === ProfileAction.Logout) {
              setShowLogoutPopup(true);
              setOpenMenu(false);
            }
            if (data.value === ProfileAction.Delete) {
              setShowDeactivatePopup(true);
              setOpenMenu(false);
            }
          }}
          onClose={() => {
            setOpenMenu(false);
          }}
        />
      </AppBarContainer>

      <StyledInformationContainer>
        <StyledInformationSectionContainer>
          <Spacing marginBottom="xl">
            <Typography variant="textBodySmallPrimary40">
              Personal Information
            </Typography>
          </Spacing>
          <ItemInfoList items={userInformation} />
        </StyledInformationSectionContainer>
        <StyledInformationSectionContainer>
          <Spacing marginTop="xl" marginBottom="xl">
            <Typography variant="textBodySmallPrimary40">
              School Information
            </Typography>
          </Spacing>
          <ItemInfoList items={schoolInformation} />
        </StyledInformationSectionContainer>
      </StyledInformationContainer>

      <Spacing marginTop="xxl" marginBottom="xxl">
        <IconText
          text={`To update your profile details contact your ${selectedRegion?.name} agent`}
          textVariant="textBodyMediumPrimary"
          icon="error"
          iconColor="--md-sys-color-light-primary"
        />
      </Spacing>

      {appVersion && (
        <>
          <Divider />
          <Spacing marginTop="xl">
            <Typography variant="textBodyMediumNeutral10">
              App Version: {appVersion}
            </Typography>
            {downloadStatus && (
              <DownloadingStatus downloadStatus={downloadStatus} />
            )}
          </Spacing>
        </>
      )}

      {process.env.NX_PUBLIC_ENV === 'local' && (
        <Spacing margin="xxxl">
          <Button
            children="Refresh App"
            variant="outlined"
            type="button"
            onClick={handleResetButtonClicked}
          ></Button>
        </Spacing>
      )}
      {/*dialogs*/}
      <Dialog
        open={showLogoutPopup}
        onClose={() => {
          setShowLogoutPopup(false);
        }}
        title="Logout?"
      >
        <Typography variant="textBodyMedium">
          Are you sure you want to logout?
        </Typography>
        <ActionButtonContainer>
          <Button
            variant="outlined"
            children="Cancel"
            onClick={() => {
              setShowLogoutPopup(false);
            }}
          />
          <Button
            children="Logout"
            onClick={() => {
              setShowLogoutPopup(false);
              onLogoutClick();
            }}
          />
        </ActionButtonContainer>
      </Dialog>
      <Dialog
        open={showResetPopup}
        onClose={() => {
          setShowResetPopup(false);
        }}
        title="Refresh App"
      >
        <Typography variant="textBodyMedium">
          Are you sure you want to refresh your app?
        </Typography>
        <ActionButtonContainer>
          <Button
            variant="outlined"
            children="Cancel"
            onClick={() => {
              setShowResetPopup(false);
            }}
          />

          <Button
            children="Refresh App"
            onClick={() => {
              setShowResetPopup(false);
              onResetAppClick();
            }}
          />
        </ActionButtonContainer>
      </Dialog>
      <AlertDialog
        title="No Internet Connection"
        description="Seems like your internet is not connected. Connect and try again."
        showPopup={showNetworkError}
        onClosePopup={() => setShowNetworkError(false)}
      />

      <AlertDialog
        title="Delete Account"
        description="Are you sure you want to delete your NIETE account? You will no longer have access to NIETE."
        showPopup={showDeactivatePopup}
        onPrimaryButtonClick={() => handleDeactivate()}
        onClosePopup={() => setShowDeactivatePopup(false)}
        onSecondaryButtonClick={() => setShowDeactivatePopup(false)}
        secondaryButton={true}
        secondaryButtonText="Cancel"
        primaryButtonText="Delete"
      />
    </>
  );
};

export const AppBarContainer = styled.div`
  position: relative;
`;

export const ActionButtonContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-md);
  md-filled-button {
    width: 50%;
  }
  md-outlined-button {
    width: 50%;
  }
`;
const StyledInformationSectionContainer = styled.div`
  width: 50%;
  ${mq(BreakpointSizes.xs)} {
    width: inherit;
  }
`;
const StyledInformationContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: var(--rk-spacing-xxxxxxxl);
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
    margin-top: var(--rk-spacing-lg);
  }
  flex: 1;
`;

interface DonwloadingStatusProps {
  downloadStatus: string;
}
const DownloadingStatus = ({ downloadStatus }: DonwloadingStatusProps) => {
  return (
    <StyledDiv>
      <Typography variant="textBodyMediumNeutral10">
        {downloadStatus === DownloadStatus.Downloading
          ? `${downloadStatus}...`
          : downloadStatus}
      </Typography>
    </StyledDiv>
  );
};
